import React, {useState, useEffect, useRef, useMemo} from 'react';
import PropTypes from 'prop-types';
import SearchDateTimePickers from 'common/search-controls/date-time-pickers';
import {
    Flex,
    Heading,
    Button,
    Text,
    Alert,
    Skeleton,
    Grid,
    Icon,
    Modal,
    Divider,
    ModalHeader,
    ModalBody,
    Accordion,
} from '@spothero/ui';
import useConfig from 'hooks/use-config';
import FormatUtils from 'utils/format';
import dayjs from 'utils/dayjs-timezone';
import {Link} from 'react-router-dom';
import facilityDetailsUpdateTimesButtonClicked from 'segment/events/facility-details-update-times-button-clicked';
import facilityDetailsViewNearbyClicked from 'segment/events/facility-details-view-nearby-clicked';
import usePrevious from 'hooks/use-previous';
import {
    ClockPlus as MoreTime,
    FilledInfoCircle as IconInfo,
} from '@spothero/icons';
import Price from 'common/price';
import TotalPriceBreakdownItem from '../total-price-breakdown-item/TotalPriceBreakdownItem';

export const getDurationString = ({starts, ends}) => {
    const endTime = dayjs.tz(ends);
    const startTime = dayjs.tz(starts);
    const diffInMinutes = Math.round(
        dayjs.duration(endTime.diff(startTime)).asMinutes()
    );

    const durationDays = Math.floor(diffInMinutes / 1440);
    const daysRemainder = diffInMinutes % 1440;
    const durationHours = Math.floor(daysRemainder / 60);
    const durationMinutes = Math.floor(daysRemainder % 60);

    const dayText =
        durationDays >= 1
            ? `${durationDays} ${FormatUtils.pluralize(
                  durationDays,
                  'day',
                  'days'
              )}`
            : '';

    const hourText =
        durationHours >= 1
            ? `${durationHours} ${FormatUtils.pluralize(
                  durationHours,
                  'hour',
                  'hours'
              )}`
            : '';

    const minuteText =
        durationMinutes >= 1
            ? `${durationMinutes} ${FormatUtils.pluralize(
                  durationMinutes,
                  'minute',
                  'minutes'
              )}`
            : '';

    const dayTextSeparator =
        dayText.length && (hourText.length || minuteText.length) ? ', ' : '';

    const hourTextSeparator = hourText.length && minuteText.length ? ', ' : '';

    return `${dayText}${dayTextSeparator}${hourText}${hourTextSeparator}${minuteText}`;
};

const UpdateReservationTimes = ({
    starts,
    ends,
    timezone,
    updateStartsAndEnds,
    price,
    currencyType,
    isRatesLoading,
    isRatesError,
    isRatesSuccess,
    onCheckoutClick,
    searchUrl,
    isAdmin,
    spotId,
    isAutoExtension,
    priceBreakdown,
}) => {
    const {apiDateTimeFormat} = useConfig();
    const [modalOpen, setModalOpen] = useState(false);
    const [newStarts, setNewStarts] = useState(starts);
    const [newEnds, setNewEnds] = useState(ends);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showAvailableSpotsMessage, setShowAvailableSpotsMessage] = useState(
        false
    );
    const [isInInvalidState, setIsInInvalidState] = useState(false);
    const isInitialLoad = useRef(true);
    const prevError = usePrevious(isRatesError);

    const handleClose = () => {
        setModalOpen(false);
    };

    const handleTotalClick = () => {
        setModalOpen(true);
    };

    const items = useMemo(() => {
        return priceBreakdown?.items?.map((item, i) => {
            return (
                <TotalPriceBreakdownItem
                    item={item}
                    currencyType={currencyType}
                    key={item.short_description + i}
                />
            );
        });
    }, [priceBreakdown, currencyType]);

    useEffect(() => {
        if (isRatesError) {
            setShowAvailableSpotsMessage(true);
            isInitialLoad.current = false;
        }
    }, [isRatesError]);

    useEffect(() => {
        if (isRatesSuccess) {
            setShowAvailableSpotsMessage(false);
            isInitialLoad.current = false;
        }
    }, [isRatesSuccess]);

    useEffect(() => {
        if (prevError && isRatesSuccess) {
            setShowSuccess(true);
        }
    }, [isRatesSuccess, prevError]);

    useEffect(() => {
        // if ends is changed by auto extension then we need to update the input
        setNewEnds(ends);
    }, [ends]);

    const durationString = useMemo(() => getDurationString({starts, ends}), [
        starts,
        ends,
    ]);

    const handleViewNearbyClick = () => {
        facilityDetailsViewNearbyClicked({spotId});
    };

    const handleDateTimeChange = ({start, end, isValidStart}) => {
        setIsInInvalidState(false);
        setShowSuccess(false);

        if (!isValidStart) {
            setIsInInvalidState(true);

            return;
        }

        setNewStarts(start.format(apiDateTimeFormat));
        setNewEnds(end.format(apiDateTimeFormat));
    };

    const handleCheckAvailabilityClick = () => {
        if (isRatesLoading) {
            return;
        }

        facilityDetailsUpdateTimesButtonClicked({
            spotId,
            editedStartTime: starts !== newStarts,
            editedEndTime: ends !== newEnds,
        });

        updateStartsAndEnds({
            starts: newStarts,
            ends: newEnds,
        });
    };

    const needToCheckAvailability = starts !== newStarts || ends !== newEnds;
    const showCheckAvailabilityButton =
        needToCheckAvailability ||
        isRatesError ||
        (!isRatesSuccess && !isInitialLoad.current);
    const buttonDisabled = isInInvalidState || isRatesLoading;

    const getRateDetails = () => {
        if (isRatesLoading) {
            return (
                <Grid
                    marginTop={4}
                    data-testid="UpdateReservationTimes-loading"
                    gap={3}
                    templateColumns="7.5rem 1fr"
                >
                    <Text lineHeight="1.4">Duration</Text>
                    <Skeleton justifySelf="end" width="5.5rem" height="100%" />
                    <Text lineHeight="1.4">Subtotal</Text>
                    <Skeleton justifySelf="end" width="4.5rem" height="100%" />
                </Grid>
            );
        } else if (needToCheckAvailability) {
            return (
                <Alert
                    marginTop={4}
                    status="neutral"
                    title="Check availability to see your price and book"
                />
            );
        } else if (isRatesError) {
            return (
                <Alert
                    marginTop={4}
                    status="error"
                    title="Sorry, the selected times are unavailable"
                    description="Choose another time or try nearby spots."
                />
            );
        } else if (isRatesSuccess) {
            const displayPrice = `${FormatUtils.currencySymbol(currencyType)}${
                FormatUtils.price(price).displayPrice
            }`;

            return (
                <>
                    {showSuccess ? (
                        <Alert
                            marginTop={4}
                            status="success"
                            title="These times are available"
                        />
                    ) : null}
                    {isAutoExtension ? (
                        <Alert
                            marginTop={4}
                            status="neutral"
                            title="You're getting more time for free"
                            description="We've extended your time at no additional cost. Happy parking!"
                            icon={MoreTime}
                        />
                    ) : null}
                    <Grid marginTop={4} gap={3} templateColumns="7.5rem 1fr">
                        <Text lineHeight="1.4">Duration</Text>
                        <Text
                            textAlign="right"
                            fontWeight="semibold"
                            lineHeight="1.4"
                        >
                            {durationString}
                        </Text>
                        <Button
                            onClick={handleTotalClick}
                            variant="tertiary"
                            color="black"
                            fontWeight="normal"
                            lineHeight="1.4"
                            justifyContent="flex-start"
                            width={16}
                        >
                            Total{' '}
                            <Icon
                                as={IconInfo}
                                color="text.secondary.light"
                                marginLeft={1}
                                lineHeight="1.4"
                                height={5}
                                width={5}
                                display="inline-block"
                            />
                        </Button>
                        <Text
                            textAlign="right"
                            fontWeight="semibold"
                            lineHeight="1.4"
                        >
                            {displayPrice}
                        </Text>
                    </Grid>
                </>
            );
        }
    };

    return (
        <>
            <Flex
                marginY={{base: 0, tablet: 4, desktop: 0}}
                maxWidth={{base: 'none', tablet: '28.5rem'}}
                borderRadius={{base: 'none', tablet: 'lg'}}
                shadow={{base: 'none', tablet: 'lg'}}
                borderColor={{base: 'gray.100', tablet: 'gray.medium'}}
                borderWidth="1px"
                borderLeftWidth={{base: '0', tablet: '1px'}}
                borderRightWidth={{base: '0', tablet: '1px'}}
                direction="column"
                backgroundColor="white"
                paddingY={6}
                paddingX={{base: 4, tablet: 6}}
                sx={{
                    '.chakra-alert__title': {
                        fontWeight: 'semibold',
                    },

                    '.FormElement-control .FormElement-item': {
                        borderColor:
                            isRatesError &&
                            !isRatesLoading &&
                            !needToCheckAvailability
                                ? 'error'
                                : null,
                    },
                    '.DatePicker-input-container > span': {
                        width: '100%',
                    },
                }}
                data-testid="UpdateReservationTimes"
                className="UpdateReservationTimes"
            >
                <Heading
                    display={{base: 'none', tablet: 'block'}}
                    marginBottom={6}
                    lineHeight="1.4"
                    fontSize="lg"
                >
                    Book Parking Now
                </Heading>
                <SearchDateTimePickers
                    isAdmin={isAdmin}
                    timezone={timezone}
                    isMonthly={false}
                    starts={starts}
                    ends={ends}
                    isStartReadonly={false}
                    onChange={handleDateTimeChange}
                    parentClassname=".UpdateReservationTimes"
                />
                {getRateDetails()}
                {showCheckAvailabilityButton ? (
                    <Button
                        disabled={buttonDisabled}
                        isLoading={isRatesLoading}
                        onClick={handleCheckAvailabilityClick}
                        marginTop={6}
                        loadingText={'Check Availability'}
                        data-testid="UpdateReservationTimes-check-availability-button"
                    >
                        Check Availability
                    </Button>
                ) : (
                    <Button
                        width="100%"
                        disabled={buttonDisabled}
                        isLoading={isRatesLoading}
                        marginTop={6}
                        loadingText={'Continue to Checkout'}
                        data-testid="UpdateReservationTimes-book-now-button"
                        textTransform="none"
                        onClick={onCheckoutClick}
                    >
                        Continue to Checkout
                    </Button>
                )}

                <Link to={searchUrl}>
                    <Button
                        data-testid="UpdateReservationTimes-view-nearby-spots-button"
                        width="100%"
                        marginTop={2.5}
                        variant="secondary"
                        onClick={handleViewNearbyClick}
                    >
                        {showAvailableSpotsMessage
                            ? 'View Available Spots Nearby'
                            : 'View Nearby Spots'}
                    </Button>
                </Link>
            </Flex>
            <Modal hasHeader size="sm" isOpen={modalOpen} onClose={handleClose}>
                <ModalHeader textAlign="center" fontSize="lg">
                    Price Breakdown
                </ModalHeader>
                <ModalBody
                    flexDirection="column"
                    paddingX={6}
                    overflow="visible"
                >
                    <Accordion allowToggle>{items}</Accordion>
                    <Divider />
                    <Flex
                        justifyContent="space-between"
                        paddingBottom={10}
                        fontWeight="bold"
                        marginY={2}
                    >
                        <Text fontWeight="bold">Total</Text>
                        <Price
                            amount={price}
                            currencyType={currencyType}
                            showCents
                        />
                    </Flex>
                </ModalBody>
            </Modal>
        </>
    );
};

UpdateReservationTimes.propTypes = {
    isRatesLoading: PropTypes.bool.isRequired,
    isRatesError: PropTypes.bool.isRequired,
    isRatesSuccess: PropTypes.bool.isRequired,
    starts: PropTypes.string,
    ends: PropTypes.string,
    timezone: PropTypes.string,
    updateStartsAndEnds: PropTypes.func,
    price: PropTypes.number,
    currencyType: PropTypes.string,
    onCheckoutClick: PropTypes.func,
    searchUrl: PropTypes.string,
    isAdmin: PropTypes.bool,
    spotId: PropTypes.number,
    isAutoExtension: PropTypes.bool,
    priceBreakdown: PropTypes.object,
};

export default UpdateReservationTimes;
